<style>
@import "@/assets/css/custom.css";

/* Allow staff suggestion dropdown to escape the table wrapper */
.salary-table-responsive {
	overflow-x: auto;
	overflow-y: visible;
}
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="Payment Out" />

		<div class="container-fluid">
			<div class="card">
				<div class="card-body">
					<form @submit.prevent="submit">
						<div class="row">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Expense type<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.expense_type" @change="onExpenseTypeChange">
									<option value="">— Select —</option>
									<option value="5">Salary</option>
									<option value="6">Travel Expense</option>
									<option value="7">Maintenance Expense</option>
									<option value="8">Food Expense</option>
									<option value="9">Printing and Stationery Expense</option>
									<option value="10">Miscellaneous Expense</option>
									<option value="11">Electricity Recharge Expense</option>
									<option value="12">Rent</option>
									<option value="15">Other</option>
									<option value="17">Courier Expense</option>
								</select>
								<div v-if="errors.expense_type" class="text-danger small mt-1">{{ errors.expense_type }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Bank account<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.bank_account_id">
									<option value="">— Select bank —</option>
									<option v-for="b in bankAccounts" :key="b.id" :value="String(b.id)">
										{{ b.bank_name }} — {{ b.account_number }}
									</option>
								</select>
								<div v-if="errors.bank_account_id" class="text-danger small mt-1">{{ errors.bank_account_id }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment date<span class="text-danger">*</span></label>
								<input class="form-control" type="date" v-model="form.payment_date" />
								<div v-if="errors.payment_date" class="text-danger small mt-1">{{ errors.payment_date }}</div>
							</div>
						</div>

						<div class="row">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment / transaction ID</label>
								<input class="form-control" type="text" v-model="form.payment_id" placeholder="UTR / reference" />
								<div v-if="errors.payment_id" class="text-danger small mt-1">{{ errors.payment_id }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Paid to name</label>
								<input class="form-control" type="text" v-model="form.user_name" placeholder="Optional" />
								<div v-if="errors.user_name" class="text-danger small mt-1">{{ errors.user_name }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Paid to mobile</label>
								<input class="form-control" v-model="form.user_mobile" v-mobile-limit placeholder="Optional" maxlength="10" minlength="10" />
								<div v-if="errors.user_mobile" class="text-danger small mt-1">{{ errors.user_mobile }}</div>
							</div>
						</div>

						<!-- Salary -->
						<div v-if="form.expense_type === '5'" class="mt-3">
							<div class="d-flex justify-content-between align-items-center border-bottom pb-2">
								<h6 class="mb-0">Salary allocation</h6>
								<button type="button" class="btn btn-sm btn-primary" @click="addSalaryRow">Add staff</button>
							</div>

							<div class="table-responsive mt-3 salary-table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th style="min-width: 260px">Staff</th>
											<th style="min-width: 140px">Amount (₹)</th>
											<th style="width: 80px">Remove</th>
										</tr>
									</thead>
									<tbody v-if="salaryRows.length">
										<tr v-for="(row, idx) in salaryRows" :key="'sr-' + idx" align="center">
											<td class="text-start">
												<input
													type="text"
													class="form-control form-control-sm"
													v-model="row.staff_search"
													placeholder="Search staff…"
													@focus="openStaffDropdown(idx, $event)"
													@input="openStaffDropdown(idx, $event)"
													@blur="scheduleCloseStaffDropdown"
												/>

												<small v-if="row.staff_id" class="text-muted d-block mt-1">
													Selected: {{ staffLabelById(row.staff_id) }}
												</small>
											</td>
											<td>
												<input
													class="form-control form-control-sm"
													type="number"
													min="0"
													step="0.01"
													v-model="row.amount"
													@input="syncTotals"
													placeholder="0.00"
												/>
											</td>
											<td>
												<button type="button" class="btn btn-sm btn-danger" @click="removeSalaryRow(idx)">X</button>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="3" class="text-center text-muted py-3">Add at least one staff</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div v-if="errors.staff_ids" class="text-danger small">{{ errors.staff_ids }}</div>
							<div v-if="errors.salary_amounts" class="text-danger small">{{ errors.salary_amounts }}</div>

							<div class="row mt-2">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Total amount (₹)<span class="text-danger">*</span></label>
									<input class="form-control" type="number" min="0" step="0.01" v-model="form.total_amount" readonly/>
									<div v-if="errors.total_amount" class="text-danger small mt-1">{{ errors.total_amount }}</div>
								</div>
							</div>
						</div>

						<!-- Non-salary -->
						<div v-else-if="form.expense_type" class="mt-3">
							<h6 class="border-bottom pb-2">Expense details</h6>
							<div class="row">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Amount (₹)</label>
									<input class="form-control" type="number" min="0" step="0.01" v-model="form.amount" @input="syncTotals" placeholder="0.00" />
									<div v-if="errors.amount" class="text-danger small mt-1">{{ errors.amount }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">GST amount (₹)</label>
									<input class="form-control" type="number" min="0" step="0.01" v-model="form.gst_amount" @input="syncTotals" placeholder="0.00" />
									<div v-if="errors.gst_amount" class="text-danger small mt-1">{{ errors.gst_amount }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Total amount (₹)<span class="text-danger">*</span></label>
									<input class="form-control" type="number" min="0" step="0.01" v-model="form.total_amount" placeholder="0.00" readonly />
									<small class="text-muted">Auto-calculated as amount + GST.</small>
									<div v-if="errors.total_amount" class="text-danger small mt-1">{{ errors.total_amount }}</div>
								</div>
							</div>
						</div>

						<div class="row">
							<div class="col-md-12 form-group mt-3">
								<label class="control-label">Remarks</label>
								<textarea class="form-control" rows="2" v-model="form.remarks" placeholder="Optional remarks"></textarea>
								<div v-if="errors.remarks" class="text-danger small mt-1">{{ errors.remarks }}</div>
							</div>
						</div>

						<div class="row mt-4">
							<div class="col-12">
								<button type="submit" class="btn btn-primary" :disabled="submitting">
									<span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									{{ submitting ? "Saving..." : "Save expense" }}
								</button>
								<button type="button" class="btn btn-light ms-2" :disabled="submitting" @click="resetAll">Reset</button>
							</div>
						</div>

						<!-- Staff dropdown portal (prevents table overflow clipping) -->
						<div
							v-if="staffDropdown.open && salaryRows[staffDropdown.idx]"
							class="list-group"
							:style="staffDropdownStyle"
							@mousedown.prevent
						>
							<button
								v-for="s in filteredStaffs(salaryRows[staffDropdown.idx].staff_search)"
								:key="'stp-' + s.id"
								type="button"
								class="list-group-item list-group-item-action"
								@mousedown.prevent
								@click="selectStaff(staffDropdown.idx, s); closeStaffDropdown()"
							>
								<div class="d-flex justify-content-between">
									<span>{{ s.name }}</span>
									<small class="text-muted">{{ s.mobile || "—" }}</small>
								</div>
							</button>
							<div v-if="!filteredStaffs(salaryRows[staffDropdown.idx].staff_search).length" class="list-group-item text-muted">No matches</div>
						</div>
					</form>
				</div>
			</div>

			<div class="card mt-4">
				<div class="card-body">
					<h5 class="card-title mb-3">Expense records</h5>

					<div class="col-md-12 project-list filter-sort">
						<div class="row align-items-end">
							<div class="col-md-6 d-flex mt-5">
								<label class="d-flex listing-length ms-1 align-items-center">
									<span class="me-2">Show</span>
									<select
										class="form-select"
										style="width: auto; min-width: 4.5rem"
										v-model="expenseList.listing_length"
										@change="fetchExpenseList()"
									>
										<option value="5">5</option>
										<option value="10">10</option>
										<option value="20">20</option>
										<option value="50">50</option>
									</select>
									<span class="ms-2">entries</span>
								</label>
							</div>
							<div class="col-md-2 mt-2">
								<label class="form-label small mb-1">Bank</label>
								<select class="form-select" v-model="expenseList.bank_account_id" @change="fetchExpenseList()">
									<option value="">All banks</option>
									<option v-for="b in bankAccounts" :key="'ex-' + b.id" :value="String(b.id)">
										{{ b.bank_name }} — {{ b.account_number }}
									</option>
								</select>
							</div>
							<div class="col-md-2 mt-2">
								<label class="form-label small mb-1">Date</label>
								<input class="form-control" type="date" v-model="expenseList.date" @change="fetchExpenseList()" />
							</div>
							<div class="col-md-2 mt-2">
								<label class="form-label small mb-1">Search</label>
								<input
									class="form-control"
									type="text"
									placeholder="Keyword…"
									v-model="expenseList.keyword"
									@input="onExpenseListKeywordInput"
								/>
							</div>
						</div>
					</div>

					<div class="table-responsive product-table mt-3">
						<table class="display table table-sm">
							<thead>
								<tr align="center">
									<th>Sl.No</th>
									<th>Transaction amount</th>
									<th>Payment type</th>
									<th>Expense type</th>
									<th>Bank</th>
									<th>Paid to</th>
									<th>Remarks</th>
									<th>Added by</th>
									<th>Payment ID</th>
									<th>Recorded on</th>
									<th>Action</th>
								</tr>
							</thead>
							<tbody v-if="expenseList.loading">
								<tr>
									<td colspan="11" align="center">
										<span class="spinner-border spinner-border-sm me-2"></span>
										Loading…
									</td>
								</tr>
							</tbody>
							<tbody v-else-if="expenseList.rows.length">
								<tr align="center" v-for="(row, index) in expenseList.rows" :key="'ex-' + row.id">
									<td>{{ expenseList.from != null ? expenseList.from + index : index + 1 }}</td>
									<td class="text-start small">
										<div><span class="text-muted">Amount:</span> ₹{{ formatMoney(row.amount) }}</div>
										<div><span class="text-muted">GST:</span> ₹{{ formatMoney(row.gst) }}</div>
										<div class="fw-bold"><span class="text-muted">Total:</span> ₹{{ formatMoney(row.total) }}</div>
									</td>
									<td>
										<span class="badge rounded-pill bg-info text-dark">{{ expensePaymentTypeLabel(row.payment_type) }}</span>
									</td>
									<td>
										<span class="badge rounded-pill bg-secondary">{{ expenseTransactionTypeLabel(row.transaction_type) }}</span>
									</td>
									<td class="text-start small">{{ row.from_account_bank_name || "—" }}</td>
									<td class="text-start small">
										<div>{{ row.user_name || "—" }}</div>
										<div class="text-muted">{{ row.user_mobile || "—" }}</div>
									</td>
									<td class="text-start small">{{ row.remarks || "—" }}</td>
									<td class="text-start small">{{ row.added_by_name || "—" }}</td>
									<td class="text-start small">{{ row.payment_id || "—" }}</td>
									<td class="text-start small">{{ row.converted_created_at || "—" }}</td>
									<td>
										<div class="d-flex flex-column align-items-center gap-1">
											<button
												v-if="String(row.transaction_type) === '5'"
												type="button"
												class="btn btn-sm btn-outline-primary"
												@click="openSalaryLogsModal(row)"
											>
												Salary logs
												<span v-if="(row.salary_logs || []).length" class="badge bg-primary ms-1">{{
													(row.salary_logs || []).length
												}}</span>
											</button>
											<button
												v-if="$canRevoke()"
												type="button"
												class="btn btn-sm btn-outline-danger"
												:disabled="revokeExpenseModal.submitting && revokeExpenseModal.row?.id === row.id"
												@click="openRevokeExpenseModal(row)"
											>
												<span v-if="revokeExpenseModal.submitting && revokeExpenseModal.row?.id === row.id" class="spinner-border spinner-border-sm me-1"></span>
												Revoke
											</button>
										</div>
									</td>
								</tr>
							</tbody>
							<tbody v-else>
								<tr>
									<td colspan="11" align="center">No records found</td>
								</tr>
							</tbody>
						</table>
					</div>

					<caption v-if="expenseList.rows.length" class="float-start mt-2 d-block">
						Showing {{ expenseList.from }} to {{ expenseList.to }} of {{ expenseList.total }} entries
					</caption>
					<nav v-if="expenseList.rows.length && expenseList.pagination.length" aria-label="Expense pagination" class="mt-2 float-end">
						<ul class="pagination mb-0">
							<li
								class="page-item"
								v-bind:class="[{ disabled: !page.url }, { active: page.active }]"
								v-for="(page, index) in expenseList.pagination"
								:key="'exp-' + index + '-' + page.label"
							>
								<a class="page-link" href="#" @click.prevent="onExpenseListPageClick(page)" tabindex="-1">
									<span v-if="index === 0">Prev</span>
									<span v-else-if="index === expenseList.pagination.length - 1">Next</span>
									<span v-else>{{ page.label }}</span>
								</a>
							</li>
						</ul>
					</nav>
				</div>
			</div>
		</div>

		<Teleport to="body">
			<div
				v-if="salaryLogsModal.open && salaryLogsModal.expense"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				style="background-color: rgba(0, 0, 0, 0.5)"
				@click.self="closeSalaryLogsModal"
			>
				<div class="modal-dialog modal-lg modal-dialog-scrollable" @click.stop>
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Salary breakdown</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closeSalaryLogsModal"></button>
						</div>
						<div class="modal-body">
							<div class="small text-muted mb-3">
								<div><strong>Expense type:</strong> {{ expenseTransactionTypeLabel(salaryLogsModal.expense.transaction_type) }}</div>
								<div><strong>Bank:</strong> {{ salaryLogsModal.expense.from_account_bank_name || "—" }}</div>
								<div><strong>Total:</strong> ₹{{ formatMoney(salaryLogsModal.expense.total) }}</div>
								<div><strong>Payment ID:</strong> {{ salaryLogsModal.expense.payment_id || "—" }}</div>
								<div><strong>Remarks:</strong> {{ salaryLogsModal.expense.remarks || "—" }}</div>
								<div><strong>Recorded:</strong> {{ salaryLogsModal.expense.converted_created_at || "—" }}</div>
							</div>
							<p v-if="!(salaryLogsModal.expense.salary_logs || []).length" class="text-muted mb-0">No per-staff salary lines on this record.</p>
							<div v-else class="table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th>Staff</th>
											<th>Amount (₹)</th>
											<th>Payment date</th>
											<th>Remarks</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="log in salaryLogsModal.expense.salary_logs || []" :key="'sl-' + log.id" align="center">
											<td class="text-start">{{ log.staff?.name || "—" }}</td>
											<td>₹{{ formatMoney(log.salary_amount) }}</td>
											<td>{{ log.payment_date ? $filters.formatDate(log.payment_date) : "—" }}</td>
											<td class="text-start small">{{ log.remarks || "—" }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-light" @click="closeSalaryLogsModal">Close</button>
						</div>
					</div>
				</div>
			</div>

			<div
				v-if="revokeExpenseModal.open && revokeExpenseModal.row"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				style="background-color: rgba(0, 0, 0, 0.5)"
				@click.self="closeRevokeExpenseModal"
			>
				<div class="modal-dialog modal-dialog-scrollable" @click.stop>
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Revoke transaction</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closeRevokeExpenseModal"></button>
						</div>
						<div class="modal-body">
							<p class="mb-3">Are you sure you want to revoke this expense transaction?</p>
							<div class="small border rounded p-3 bg-light text-dark">
								<!-- <div class="mb-1"><strong>Transaction ID:</strong> #{{ revokeExpenseModal.row.id }}</div> -->
								<div class="mb-1"><strong>Expense type:</strong> {{ expenseTransactionTypeLabel(revokeExpenseModal.row.transaction_type) }}</div>
								<div class="mb-1"><strong>Payment type:</strong> {{ expensePaymentTypeLabel(revokeExpenseModal.row.payment_type) }}</div>
								<div class="mb-1"><strong>Amount:</strong> ₹{{ formatMoney(revokeExpenseModal.row.amount) }}</div>
								<div class="mb-1"><strong>GST:</strong> ₹{{ formatMoney(revokeExpenseModal.row.gst) }}</div>
								<div class="mb-1"><strong>Total:</strong> ₹{{ formatMoney(revokeExpenseModal.row.total) }}</div>
								<div class="mb-1"><strong>Bank:</strong> {{ revokeExpenseModal.row.from_account_bank_name || "—" }}</div>
								<div class="mb-1"><strong>Paid to:</strong> {{ revokeExpenseModal.row.user_name || "—" }}</div>
								<div class="mb-1"><strong>Mobile:</strong> {{ revokeExpenseModal.row.user_mobile || "—" }}</div>
								<div class="mb-1"><strong>Payment ID:</strong> {{ revokeExpenseModal.row.payment_id || "—" }}</div>
								<div class="mb-0"><strong>Remarks:</strong> {{ revokeExpenseModal.row.remarks || "—" }}</div>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-danger" :disabled="revokeExpenseModal.submitting" @click="confirmRevokeExpense">
								<span v-if="revokeExpenseModal.submitting" class="spinner-border spinner-border-sm me-2"></span>
								Yes, revoke
							</button>
							<button type="button" class="btn btn-light" :disabled="revokeExpenseModal.submitting" @click="closeRevokeExpenseModal">No</button>
						</div>
					</div>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<script>
export default {
	name: "PaymentOut",
	data() {
		return {
			token: "",
			submitting: false,
			errors: {},

			bankAccounts: [],
			staffs: [],
			salaryRows: [],
			staffDropdown: {
				open: false,
				idx: -1,
				top: 0,
				left: 0,
				width: 0,
			},
			closeStaffDropdownTimer: null,

			form: {
				expense_type: "",
				staff_ids: [],
				salary_amounts: [],
				bank_account_id: "",
				amount: "",
				gst_amount: "",
				total_amount: "",
				payment_id: "",
				payment_date: "",
				remarks: "",
				user_name: "",
				user_mobile: "",
			},

			expenseList: {
				listing_length: "10",
				keyword: "",
				date: "",
				bank_account_id: "",
				rows: [],
				pagination: [],
				from: 0,
				to: 0,
				total: 0,
				loading: false,
				active_page_url: "",
			},
			expenseListKeywordDebounceTimer: null,

			salaryLogsModal: {
				open: false,
				expense: null,
			},

			revokeExpenseModal: {
				open: false,
				row: null,
				submitting: false,
			},
		};
	},
	computed: {
		staffDropdownStyle() {
			return {
				position: "fixed",
				top: `${this.staffDropdown.top}px`,
				left: `${this.staffDropdown.left}px`,
				width: `${this.staffDropdown.width}px`,
				zIndex: 2000,
				maxHeight: "220px",
				overflow: "auto",
			};
		},
	},
	created() {
		this.init();
	},
	mounted() {
		window.addEventListener("scroll", this.closeStaffDropdown, true);
		window.addEventListener("resize", this.closeStaffDropdown);
	},
	beforeUnmount() {
		window.removeEventListener("scroll", this.closeStaffDropdown, true);
		window.removeEventListener("resize", this.closeStaffDropdown);
		if (this.closeStaffDropdownTimer) clearTimeout(this.closeStaffDropdownTimer);
		if (this.expenseListKeywordDebounceTimer) {
			clearTimeout(this.expenseListKeywordDebounceTimer);
			this.expenseListKeywordDebounceTimer = null;
		}
	},
	methods: {
		getToken() {
			if (localStorage.getItem("airotrack_staff")) {
				const airotrack_staff = JSON.parse(localStorage.getItem("airotrack_staff"));
				if (airotrack_staff.token) {
					this.token = airotrack_staff.token;
					return;
				}
			}
			this.$swal("", "Session expired, please login again", "error");
			this.$router.push({ name: "login" });
		},

		async init() {
			this.getToken();
			await Promise.all([this.fetchBankAccounts(), this.fetchStaffs()]);
			this.fetchExpenseList();
		},

		buildExpenseQueryString() {
			const kw = String(this.expenseList.keyword || "").trim();
			const date = String(this.expenseList.date || "").trim();
			const bankId = String(this.expenseList.bank_account_id || "").trim();
			return [
				`limit=${encodeURIComponent(this.expenseList.listing_length)}`,
				`keyword=${encodeURIComponent(kw)}`,
				`date=${encodeURIComponent(date)}`,
				`bank_account_id=${encodeURIComponent(bankId)}`,
			].join("&");
		},

		expenseListUrl() {
			return `/staffs/getExpense?${this.buildExpenseQueryString()}`;
		},

		onExpenseListKeywordInput() {
			if (this.expenseListKeywordDebounceTimer) {
				clearTimeout(this.expenseListKeywordDebounceTimer);
			}
			this.expenseListKeywordDebounceTimer = setTimeout(() => {
				this.expenseListKeywordDebounceTimer = null;
				this.fetchExpenseList();
			}, 400);
		},

		fetchExpenseList(pageUrl) {
			this.getToken();
			this.expenseList.loading = true;
			const url = pageUrl || this.expenseListUrl();
			this.expenseList.active_page_url = url;
			this.$axios
				.get(url, { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const payload = res.data?.data || {};
					const paginator = payload.expense;
					if (!paginator) {
						this.expenseList.rows = [];
						this.expenseList.pagination = [];
						this.expenseList.from = 0;
						this.expenseList.to = 0;
						this.expenseList.total = 0;
						return;
					}
					this.expenseList.rows = paginator.data || [];
					this.expenseList.pagination = paginator.links || [];
					this.expenseList.from = paginator.from ?? 0;
					this.expenseList.to = paginator.to ?? 0;
					this.expenseList.total = paginator.total ?? 0;
				})
				.catch((err) => {
					console.error(err);
					this.expenseList.rows = [];
					this.expenseList.pagination = [];
					this.expenseList.from = 0;
					this.expenseList.to = 0;
					this.expenseList.total = 0;
					this.$swal("Error", err.response?.data?.message || "Failed to load expense list", "error");
				})
				.finally(() => {
					this.expenseList.loading = false;
				});
		},

		onExpenseListPageClick(page) {
			if (!page.url) return;
			const base = page.url;
			const sep = base.includes("?") ? "&" : "?";
			this.fetchExpenseList(`${base}${sep}${this.buildExpenseQueryString()}`);
		},

		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},

		expensePaymentTypeLabel(v) {
			const map = { 1: "Credit", 2: "Debit", 3: "Contra" };
			return map[Number(v)] || "—";
		},

		expenseTransactionTypeLabel(v) {
			const map = {
				5: "Salary",
				6: "Travel Expense",
				7: "Maintenance Expense",
				8: "Food Expense",
				9: "Printing and Stationery Expense",
				10: "Miscellaneous Expense",
				11: "Electricity Recharge Expense",
				12: "Rent",
				13: "Renewal",
				14: "Work Service",
				15: "Other",
			};
			return map[Number(v)] || "—";
		},

		openSalaryLogsModal(row) {
			this.salaryLogsModal.expense = row;
			this.salaryLogsModal.open = true;
		},

		closeSalaryLogsModal() {
			this.closeRevokeExpenseModal();
			this.salaryLogsModal.open = false;
			this.salaryLogsModal.expense = null;
		},

		openRevokeExpenseModal(row) {
			if (!this.$canRevoke()) return;
			this.revokeExpenseModal.row = row;
			this.revokeExpenseModal.open = true;
			this.revokeExpenseModal.submitting = false;
		},

		closeRevokeExpenseModal() {
			this.revokeExpenseModal.open = false;
			this.revokeExpenseModal.row = null;
			this.revokeExpenseModal.submitting = false;
		},

		isApiSuccess(data) {
			return data?.status === true || data?.status === "true" || data?.success === true || data?.success === "true";
		},

		async confirmRevokeExpense() {
			if (!this.revokeExpenseModal.row?.id) return;
			this.getToken();
			this.revokeExpenseModal.submitting = true;
			const formData = new FormData();
			formData.append("airo_payment_transaction_id", this.revokeExpenseModal.row.id);
			try {
				const res = await this.$axios.post("/staffs/revokeExpense", formData, {
					headers: {
						"content-type": "multipart/form-data",
						Authorization: "Bearer " + this.token,
					},
				});
				if (this.isApiSuccess(res.data)) {
					this.$swal("Success", res.data?.message || "Transaction revoked", "success");
					this.closeRevokeExpenseModal();
					this.fetchExpenseList(this.expenseList.active_page_url);
				} else {
					this.$swal("Error", res.data?.message || "Failed to revoke transaction", "error");
				}
			} catch (err) {
				this.$swal("Error", err?.response?.data?.message || "Failed to revoke transaction", "error");
			} finally {
				this.revokeExpenseModal.submitting = false;
			}
		},

		fetchBankAccounts() {
			return this.$axios
				.get("/staffs/bankAccounts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.bankAccounts = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.bankAccounts = [];
				});
		},

		fetchStaffs() {
			return this.$axios
				.get("/staffs/getStaffs", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const d = res.data?.data || {};
					this.staffs = Array.isArray(d.staffs) ? d.staffs : [];
				})
				.catch(() => {
					this.staffs = [];
				});
		},

		onExpenseTypeChange() {
			this.errors = {};
			this.salaryRows = [];
			this.form.amount = "";
			this.form.gst_amount = "";
			this.form.total_amount = "";
			this.form.staff_ids = [];
			this.form.salary_amounts = [];
			if (this.form.expense_type === "5") {
				this.addSalaryRow();
			}
		},

		addSalaryRow() {
			this.salaryRows.push({ staff_id: "", staff_search: "", amount: "" });
			this.syncTotals();
		},
		openStaffDropdown(idx, evt) {
			if (!this.salaryRows[idx]) return;
			if (this.closeStaffDropdownTimer) {
				clearTimeout(this.closeStaffDropdownTimer);
				this.closeStaffDropdownTimer = null;
			}
			const el = evt?.target;
			if (!el || !el.getBoundingClientRect) return;
			const r = el.getBoundingClientRect();
			this.staffDropdown.open = true;
			this.staffDropdown.idx = idx;
			this.staffDropdown.top = Math.round(r.bottom + 2);
			this.staffDropdown.left = Math.round(r.left);
			this.staffDropdown.width = Math.round(r.width);
		},

		scheduleCloseStaffDropdown() {
			if (this.closeStaffDropdownTimer) clearTimeout(this.closeStaffDropdownTimer);
			this.closeStaffDropdownTimer = setTimeout(() => {
				this.closeStaffDropdown();
			}, 180);
		},

		closeStaffDropdown() {
			this.staffDropdown.open = false;
			this.staffDropdown.idx = -1;
		},

		filteredStaffs(search) {
			const q = String(search || "").toLowerCase().trim();
			if (!q) return this.staffs.slice(0, 20);
			return this.staffs
				.filter((s) => {
					const name = String(s.name || "").toLowerCase();
					const mobile = String(s.mobile || "").toLowerCase();
					return name.includes(q) || mobile.includes(q);
				})
				.slice(0, 20);
		},

		selectStaff(idx, staff) {
			if (!this.salaryRows[idx]) return;
			this.salaryRows[idx].staff_id = String(staff?.id || "");
			this.salaryRows[idx].staff_search = staff?.name ? String(staff.name) : "";
		},

		staffLabelById(id) {
			const s = this.staffs.find((x) => String(x.id) === String(id));
			if (!s) return "#" + String(id);
			return `${s.name}${s.mobile ? " (" + s.mobile + ")" : ""}`;
		},

		removeSalaryRow(idx) {
			this.salaryRows.splice(idx, 1);
			this.syncTotals();
		},

		syncTotals() {
			if (this.form.expense_type === "5") {
				const sum = this.salaryRows.reduce((t, r) => {
					const n = parseFloat(r.amount);
					return !isNaN(n) && n >= 0 ? t + n : t;
				}, 0);
				this.form.total_amount = sum.toFixed(2);
				return;
			}

			const a = parseFloat(this.form.amount);
			const g = parseFloat(this.form.gst_amount);
			const hasAmount = !isNaN(a);
			const hasGst = !isNaN(g);
			if (!hasAmount && !hasGst) {
				this.form.total_amount = "";
				return;
			}
			const total = (hasAmount ? a : 0) + (hasGst ? g : 0);
			this.form.total_amount = total.toFixed(2);
		},

		validate() {
			this.errors = {};
			if (!this.form.expense_type) {
				this.errors.expense_type = "Select expense type";
				return false;
			}
			if (!this.form.bank_account_id) {
				this.errors.bank_account_id = "Select bank account";
				return false;
			}
			if (!this.form.payment_date) {
				this.errors.payment_date = "Select payment date";
				return false;
			}

			const total = parseFloat(this.form.total_amount);
			if (isNaN(total) || total < 0) {
				this.errors.total_amount = "Enter a valid total amount";
				return false;
			}

			if (this.form.expense_type === "5") {
				if (!this.salaryRows.length) {
					this.errors.staff_ids = "Add at least one staff";
					return false;
				}
				for (const r of this.salaryRows) {
					if (!r.staff_id) {
						this.errors.staff_ids = "Select staff for all rows";
						return false;
					}
					const n = parseFloat(r.amount);
					if (isNaN(n) || n < 0) {
						this.errors.salary_amounts = "Enter valid amounts for all rows";
						return false;
					}
				}
			}

			return true;
		},

		buildPayload() {
			const payload = {
				expense_type: parseInt(this.form.expense_type, 10),
				bank_account_id: parseInt(this.form.bank_account_id, 10),
				amount: this.form.amount === "" ? null : parseFloat(this.form.amount),
				gst_amount: this.form.gst_amount === "" ? null : parseFloat(this.form.gst_amount),
				total_amount: parseFloat(this.form.total_amount),
				payment_id: String(this.form.payment_id || "").trim() || null,
				payment_date: this.form.payment_date,
				remarks: String(this.form.remarks || "").trim() || null,
				user_name: String(this.form.user_name || "").trim() || null,
				user_mobile: String(this.form.user_mobile || "").trim() || null,
				staff_ids: null,
				salary_amounts: null,
			};

			if (this.form.expense_type === "5") {
				payload.staff_ids = this.salaryRows.map((r) => parseInt(r.staff_id, 10));
				payload.salary_amounts = this.salaryRows.map((r) => parseFloat(r.amount));
				// Salary uses allocator; backend still accepts nullable `amount`
				payload.amount = null;
				payload.gst_amount = null;
			}
			return payload;
		},

		resetAll() {
			this.errors = {};
			this.submitting = false;
			this.salaryRows = [];
			this.form = {
				expense_type: "",
				staff_ids: [],
				salary_amounts: [],
				bank_account_id: "",
				amount: "",
				gst_amount: "",
				total_amount: "",
				payment_id: "",
				payment_date: "",
				remarks: "",
				user_name: "",
				user_mobile: "",
			};
		},

		async submit() {
			if (this.submitting) return;
			if (!this.validate()) return;
			this.submitting = true;
			this.getToken();
			try {
				const payload = this.buildPayload();
				const res = await this.$axios.post("/staffs/createExpense", payload, {
					headers: { Authorization: "Bearer " + this.token, "Content-Type": "application/json" },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (ok) {
					this.$swal("Success", this.$extractApiMessage(res.data?.message) || "Expense created", "success");
					this.resetAll();
					this.fetchExpenseList();
				} else {
					this.$applyApiFieldErrors(this.errors, res.data);
					this.$swal("Error", this.$getApiErrorMessage(res.data, "Failed to create expense"), "error");
				}
			} catch (err) {
				this.$applyApiFieldErrors(this.errors, err);
				this.$swal("Error", this.$getApiErrorMessage(err, "Something went wrong"), "error");
			} finally {
				this.submitting = false;
			}
		},
	},
};
</script>

